import JSZip from "jszip";
import { useCallback, useState } from "react";
import useImageUtils from "../utils/useImageUtils";

interface MaskData {
	panopticImage: HTMLImageElement | null;
	maskImages: HTMLImageElement[];
	maskNumbers: number[];
}

export const useSegmentationMaskProcessor = () => {
	const [isLoading, setIsLoading] = useState(false);
	const [maskData, setMaskData] = useState<MaskData>({
		panopticImage: null,
		maskImages: [],
		maskNumbers: [],
	});

	const { loadImageFromBlob} = useImageUtils()

	const downloadAndProcessZip = useCallback(async (zipUrl: string): Promise<MaskData> => {
		setIsLoading(true);

		try {
			const response = await fetch(zipUrl);
			if (!response.ok) {
				throw new Error(`Failed to download zip: ${response.statusText}`);
			}

			const zipBlob = await response.blob();
			const zip = new JSZip();
			const zipContents = await zip.loadAsync(zipBlob);

			let panopticImage: HTMLImageElement | null = null;
			const maskImages: HTMLImageElement[] = [];
			const maskNumbers: number[] = [];

			const numberedFiles = Object.entries(zipContents.files)
				.filter(([filename, file]) =>
					!file.dir && filename.toLowerCase().endsWith(".png") && !filename.includes("panoptic")
				)
				.map(([filename]) => {
					const match = filename.match(/_(\d+)\.png$/);
					if (!match) return null;
					return { filename, number: parseInt(match[1], 10) };
				})
				.filter((f): f is { filename: string; number: number } => f !== null)
				.sort((a, b) => a.number - b.number);

			if (numberedFiles.length >= 2) {
				numberedFiles.splice(numberedFiles.length - 2, 1);
			}

			for (const { filename, number } of numberedFiles) {
				const file = zipContents.files[filename];
				const blob = await file.async("blob");
				const img = await loadImageFromBlob(blob);
				maskImages.push(img);
				maskNumbers.push(number);
			}

			for (const [filename, file] of Object.entries(zipContents.files)) {
				if (!file.dir && filename.includes("panoptic") && filename.toLowerCase().endsWith(".png")) {
					const blob = await file.async("blob");
					panopticImage = await loadImageFromBlob(blob);
					break;
				}
			}

			const result = {
				panopticImage,
				maskImages,
				maskNumbers,
			};

			setMaskData(result);
			return result;
		} catch (error) {
			console.error("Error processing zip file:", error);
			throw error;
		} finally {
			setIsLoading(false);
		}
	}, []);

	const getImagePixelsData = useCallback((image: HTMLImageElement) => {
		const canvas = document.createElement("canvas");
		canvas.width = image.width;
		canvas.height = image.height;
		const ctx = canvas.getContext("2d");

		if (!ctx) {
			throw new Error("Failed to get canvas context");
		}

		ctx.drawImage(image, 0, 0);
		return ctx.getImageData(0, 0, image.width, image.height);
	}, []);

	return {
		downloadAndProcessZip,
		getImagePixelsData,
		maskData,
		isLoading,
	};
};
